import {
    moveToNextLeafBlock,
    moveToPreviousLeafBlock,
    type TableCardsView,
    table,
} from '@gitbook/doc-core';

import type { TableViewOnKeyDownFn } from '../types';
import { relativeRecordSelectionMove, relativeSelectionMove } from '../utils';

/**
 * Handle keyboard interaction in the view.
 */
export const onKeyDown: TableViewOnKeyDownFn<TableCardsView> = (
    key,
    tableBlock,
    tableState,
    change
) => {
    if (!tableState.selection.definitionId && tableState.selection.recordId) {
        return onKeyDownAtCardLevel(key, tableBlock, tableState, change);
    }

    if (tableState.selection.definitionId && tableState.selection.recordId) {
        return onKeyDownAtColumnLevel(key, tableBlock, tableState, change);
    }
};

/**
 * When pressing key while having a column selected.
 */
const onKeyDownAtColumnLevel: TableViewOnKeyDownFn<TableCardsView> = (
    key,
    tableBlock,
    tableState,
    change
) => {
    const cardsPerRow = tableState.view.cardSize === 'medium' ? 3 : 2;

    switch (key) {
        //
        // Move to definition below/after
        //
        case 'move-up':
        case 'move-down': {
            // Move to the next/previous definition
            if (
                !relativeSelectionMove(change, tableBlock, tableState, {
                    definition: { relative: key === 'move-up' ? -1 : 1 },
                    record: { relative: 0 },
                    focus: key === 'move-up' ? 'end' : 'start',
                })
            ) {
                // If we can't then we try to move to the card above or below in the grid
                if (
                    !relativeSelectionMove(change, tableBlock, tableState, {
                        definition: {
                            absolute: key === 'move-up' ? tableState.view.columns.length - 1 : 0,
                        },
                        record: { relative: key === 'move-up' ? -cardsPerRow : cardsPerRow },
                        focus: key === 'move-up' ? 'end' : 'start',
                    })
                ) {
                    // Otherwise we move to the next/previous block, outside the table
                    if (key === 'move-up') {
                        moveToPreviousLeafBlock(change, tableBlock);
                    } else {
                        moveToNextLeafBlock(change, tableBlock);
                    }
                }
            }
            break;
        }

        //
        // Move to card on the side.
        //
        case 'move-left':
        case 'move-right': {
            relativeSelectionMove(change, tableBlock, tableState, {
                definition: {
                    absolute: 0,
                },
                record: { relative: key === 'move-left' ? -1 : 1 },
                focus: key === 'move-left' ? 'end' : 'start',
            });
            break;
        }

        //
        // Pressing enter should move to next column
        //
        case 'tab':
        case 'enter': {
            if (
                !relativeSelectionMove(change, tableBlock, tableState, {
                    definition: { relative: 1 },
                    record: { relative: 0 },
                    focus: 'end',
                })
            ) {
                if (
                    !relativeSelectionMove(change, tableBlock, tableState, {
                        definition: { absolute: 0 },
                        record: { relative: 1 },
                        focus: 'end',
                    })
                ) {
                    // Insert a new record and focus it
                    const newRecordId = table.insertEmptyRecord(change, tableBlock.key);

                    table.moveTableSelection(
                        change,
                        tableBlock,
                        {
                            recordId: newRecordId,
                            definitionId: tableState.view.columns[0],
                        },
                        'start'
                    );
                }
            }
            break;
        }

        //
        // Pressing backspace should move to previous column
        // or select the entire card
        //
        case 'backspace': {
            if (
                !relativeSelectionMove(change, tableBlock, tableState, {
                    definition: { relative: -1 },
                    record: { relative: 0 },
                    focus: 'end',
                })
            ) {
                // If we can't move to the previous column, we select the card
                table.moveTableSelection(change, tableBlock, {
                    recordId: tableState.selection.recordId,
                    definitionId: undefined,
                });
            }

            break;
        }
    }
};

/**
 * When pressing key while having an entire card selected.
 */
const onKeyDownAtCardLevel: TableViewOnKeyDownFn<TableCardsView> = (
    key,
    tableBlock,
    tableState,
    change
) => {
    const cardsPerRow = tableState.view.cardSize === 'medium' ? 3 : 2;

    switch (key) {
        case 'tab': {
            relativeRecordSelectionMove(change, tableBlock, tableState, { relative: 1 });
            break;
        }
        case 'shift+tab': {
            relativeRecordSelectionMove(change, tableBlock, tableState, { relative: -1 });
            break;
        }
        case 'move-left':
        case 'move-right': {
            if (
                !relativeRecordSelectionMove(change, tableBlock, tableState, {
                    relative: key === 'move-left' ? -1 : 1,
                })
            ) {
                // Otherwise we move to the next/previous block, outside the table
                if (key === 'move-left') {
                    moveToPreviousLeafBlock(change, tableBlock);
                } else {
                    moveToNextLeafBlock(change, tableBlock);
                }
            }
            break;
        }
        case 'move-up':
        case 'move-down': {
            if (
                !relativeRecordSelectionMove(change, tableBlock, tableState, {
                    relative: key === 'move-up' ? -cardsPerRow : cardsPerRow,
                })
            ) {
                // Otherwise we move to the next/previous block, outside the table
                if (key === 'move-up') {
                    moveToPreviousLeafBlock(change, tableBlock);
                } else {
                    moveToNextLeafBlock(change, tableBlock);
                }
            }
            break;
        }
        case 'backspace': {
            if (!relativeRecordSelectionMove(change, tableBlock, tableState, { relative: -1 })) {
                relativeRecordSelectionMove(change, tableBlock, tableState, { relative: 1 });
            }
            table.removeRecord(change, tableBlock.key, tableState.selection.recordId!);
            break;
        }
        case 'enter': {
            relativeSelectionMove(change, tableBlock, tableState, {
                definition: { absolute: 0 },
                record: { relative: 0 },
                focus: 'start',
            });
            break;
        }
    }
};
